import { useState } from 'react';
import { Search, Plus, UserCheck, Calendar, Package, MoreVertical } from 'lucide-react';
import { CanEdit, ReadOnlyBadge } from '@components/common/RequirePermission';

interface Assignment {
  id: string;
  volunteerName: string;
  organisationName: string;
  type: 'resource_delivery' | 'collection_tin' | 'event_support';
  status: 'pending' | 'in_progress' | 'completed';
  scheduledDate: string;
  resources: string;
}

// Mock data
const mockAssignments: Assignment[] = [
  {
    id: '1',
    volunteerName: 'Sarah Johnson',
    organisationName: 'St Mary\'s Hospital',
    type: 'resource_delivery',
    status: 'pending',
    scheduledDate: '2026-02-05',
    resources: '100 leaflets, 10 posters',
  },
  {
    id: '2',
    volunteerName: 'Michael Brown',
    organisationName: 'Tesco Express',
    type: 'collection_tin',
    status: 'in_progress',
    scheduledDate: '2026-02-03',
    resources: '1 collection tin (#CT-2024-089)',
  },
  {
    id: '3',
    volunteerName: 'Emma Wilson',
    organisationName: 'Manchester Town Hall',
    type: 'event_support',
    status: 'completed',
    scheduledDate: '2026-01-28',
    resources: 'Event materials pack',
  },
];

const statusStyles = {
  pending: 'bg-yellow-100 text-yellow-700',
  in_progress: 'bg-blue-100 text-blue-700',
  completed: 'bg-green-100 text-green-700',
};

const statusLabels = {
  pending: 'Pending',
  in_progress: 'In Progress',
  completed: 'Completed',
};

const typeLabels = {
  resource_delivery: 'Resource Delivery',
  collection_tin: 'Collection Tin Placement',
  event_support: 'Event Support',
};

export default function Volunteers() {
  const [searchTerm, setSearchTerm] = useState('');

  const filteredAssignments = mockAssignments.filter((assignment) =>
    assignment.volunteerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
    assignment.organisationName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="space-y-6">
      {/* Header actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div className="flex-1 max-w-md">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="search"
              placeholder="Search assignments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input pl-10"
            />
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <ReadOnlyBadge />
          <CanEdit>
            <button className="btn-primary">
              <Plus className="h-4 w-4 mr-2" />
              New Assignment
            </button>
          </CanEdit>
        </div>
      </div>

      {/* Stats overview */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Calendar className="h-5 w-5 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {mockAssignments.filter(a => a.status === 'pending').length}
              </p>
              <p className="text-sm text-gray-500">Pending</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <UserCheck className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {mockAssignments.filter(a => a.status === 'in_progress').length}
              </p>
              <p className="text-sm text-gray-500">In Progress</p>
            </div>
          </div>
        </div>
        <div className="card">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <Package className="h-5 w-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-semibold text-gray-900">
                {mockAssignments.filter(a => a.status === 'completed').length}
              </p>
              <p className="text-sm text-gray-500">Completed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Assignments table */}
      <div className="card overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Volunteer
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Organisation
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Resources
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredAssignments.map((assignment) => (
                <tr key={assignment.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="h-8 w-8 flex-shrink-0 rounded-full bg-sca-purple-100 flex items-center justify-center">
                        <span className="text-xs font-medium text-sca-purple-600">
                          {assignment.volunteerName.split(' ').map(n => n[0]).join('')}
                        </span>
                      </div>
                      <span className="ml-3 text-sm font-medium text-gray-900">
                        {assignment.volunteerName}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {assignment.organisationName}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {typeLabels[assignment.type]}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                    {assignment.resources}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(assignment.scheduledDate).toLocaleDateString('en-GB', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`px-2.5 py-0.5 text-xs font-medium rounded-full ${statusStyles[assignment.status]}`}>
                      {statusLabels[assignment.status]}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right">
                    <CanEdit>
                      <button className="text-gray-400 hover:text-gray-600">
                        <MoreVertical className="h-5 w-5" />
                      </button>
                    </CanEdit>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
